// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { ActionableNotification } from '@carbon/react';

/**
 * Marker for a surface whose last background refresh failed while an earlier
 * successful load is still on screen. react-query keeps `data` through a failed
 * refetch, so a page can show the last good state — but it must say so rather
 * than let it read as current. Pair with: error-with-Retry when there is no data
 * at all, this when there is.
 */
export function StaleDataNotice({
  what = 'data',
  error,
  onRetry,
  title,
  subtitle,
}: {
  /** Noun for the title, e.g. "alarms" → "Couldn't refresh alarms". */
  what?: string;
  error?: unknown;
  onRetry: () => void;
  /** Override the title, for a part of the page that failed rather than a refresh. */
  title?: string;
  /** Override the subtitle; the error detail is still appended. */
  subtitle?: string;
}) {
  const detail = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return (
    <ActionableNotification
      kind="warning"
      lowContrast
      role="status"
      title={title ?? `Couldn't refresh ${what}`}
      subtitle={`${subtitle ?? 'Showing the last successful load.'}${detail ? ` ${detail}` : ''}`}
      actionButtonLabel="Retry"
      onActionButtonClick={onRetry}
      hideCloseButton
    />
  );
}

export default StaleDataNotice;
